/**
 * Native Push Notifications helper for Capacitor.
 * 
 * On iOS: Uses @capacitor/push-notifications for permission/registration with APNs,
 *   then receives the FCM token from the native Firebase Messaging SDK via a
 *   custom DOM event ('fcmTokenReceived'). The FCM token is what gets sent to
 *   the backend, because firebase-admin requires FCM tokens.
 * 
 * On Android: The Capacitor plugin returns FCM tokens directly.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * Initialize push notifications on native platforms.
 * Call this after user logs in.
 * 
 * @param {Object} callbacks
 * @param {Function} callbacks.onTokenReceived - Callback with the FCM token string
 * @param {Function} callbacks.onNotificationReceived - Callback when notification arrives while app is open
 * @param {Function} callbacks.onNotificationTapped - Callback when user taps a notification
 */
export const initNativePush = async ({
  onTokenReceived,
  onNotificationReceived,
  onNotificationTapped,
  onPermissionDenied,
  onError
} = {}) => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Check current permission state first; only PROMPT when it's actually
    // promptable. On iOS a previously-denied prompt silently no-ops if you
    // call requestPermissions again, so we must detect 'denied' explicitly.
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      console.warn('[PUSH] Notification permission not granted:', permStatus.receive);
      if (onPermissionDenied) onPermissionDenied(permStatus.receive);
      return;
    }

    // Register with native push service (APNs on iOS, FCM on Android)
    await PushNotifications.register();

    // --- iOS: Listen for FCM token from native Firebase Messaging SDK ---
    if (Capacitor.getPlatform() === 'ios') {
      // Check if the FCM token was already received before this code ran
      if (window.__fcmToken && onTokenReceived) {
        console.log('[PUSH] FCM token already available:', window.__fcmToken.substring(0, 20) + '...');
        onTokenReceived(window.__fcmToken);
      }

      // Also listen for future token updates/refreshes
      const fcmHandler = (event) => {
        const fcmToken = event.detail?.token;
        if (fcmToken && onTokenReceived) {
          console.log('[PUSH] FCM token received from native bridge:', fcmToken.substring(0, 20) + '...');
          onTokenReceived(fcmToken);
        }
      };
      window.addEventListener('fcmTokenReceived', fcmHandler);

      // Store cleanup reference
      window.__fcmCleanup = () => {
        window.removeEventListener('fcmTokenReceived', fcmHandler);
      };
    }

    // Capacitor registration event
    PushNotifications.addListener('registration', (token) => {
      if (Capacitor.getPlatform() === 'ios') {
        // On iOS this is the APNs token - log it but don't send to backend
        console.log('[PUSH] APNs token received (backend uses FCM token instead):', token.value.substring(0, 20) + '...');
      } else {
        // On Android the Capacitor plugin returns the FCM token directly
        console.log('[PUSH] FCM token received:', token.value.substring(0, 20) + '...');
        if (onTokenReceived) {
          onTokenReceived(token.value);
        }
      }
    });

    // Registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[PUSH] Registration error:', error);
      if (onError) onError(error);
    });

    // Foreground notifications
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[PUSH] Notification received:', notification);
      if (onNotificationReceived) {
        onNotificationReceived(notification);
      }
    });

    // Notification tapped
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[PUSH] Notification tapped:', action);
      if (onNotificationTapped) {
        onNotificationTapped(action.notification);
      }
    });

  } catch (error) {
    console.error('[PUSH] Failed to initialize push notifications:', error);
  }
};

/**
 * Remove all push notification listeners.
 * Call this on logout.
 */
export const cleanupNativePush = async () => {
  if (!Capacitor.isNativePlatform()) return;
  
  try {
    // Clean up FCM token listener
    if (window.__fcmCleanup) {
      window.__fcmCleanup();
      delete window.__fcmCleanup;
    }
    delete window.__fcmToken;

    await PushNotifications.removeAllListeners();
  } catch (error) {
    console.error('[PUSH] Failed to cleanup push notifications:', error);
  }
};
